import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
  Filter,
  Info,
  Search,
  X,
} from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import apiClient from '@/api/client'
import { rangeToSearch, type UseReportRange } from '@/hooks/useReportRange'
import type { DailySalesRow, InvoiceSearchRow } from '@/types'
import { useCurrency } from '@/contexts/CurrencyContext'
import { useDayBoundaryHour } from '@/hooks/useDayBoundaryHour'
import { ExportButton } from './ExportButton'
import { ReportsExportSlot } from './ReportsExportSlot'
import { DayFilter } from './DayFilter'
import { OrdersBrowserTab } from './OrdersBrowserTab'
import { openPrintableReport, escapeHtml } from '@/lib/printReport'
import { useBusinessNameWithFallback } from '@/hooks/useBusinessName'
import { formatDateHuman, formatDateRangeHuman, formatHour12, toIsoDate } from '@/lib/utils'
import type { ReportTab, ReportsSearch } from './reportsRouteSearch'
import {
  activeFilterCount,
  clearFilters,
  filtersFromSearch,
  searchFromFilters,
  type OrdersBrowserFilters,
} from './ordersBrowserFilters'
import { useEnabledOrderTypes } from '@/hooks/useEnabledOrderTypes'
import { displayLabelForOrderType } from '@/lib/orderTypeSettings'

type SortKey =
  | 'date'
  | 'orders'
  | 'covers'
  | 'subtotal'
  | 'net'
  | 'gross'
  | 'discounts'
  | 'service_charge'
  | 'tax'
type SortDir = 'asc' | 'desc'

interface Props {
  range: UseReportRange
}

export function DailySalesTab({ range }: Props) {
  const { formatCurrency } = useCurrency()
  const navigate = useNavigate()
  const search = useSearch({ from: '/admin/reports' }) as ReportsSearch
  const [sortSearch, setSortSearch] = useState('')
  const [sortKey, setSortKey] = useState<SortKey>('date')
  const [sortDir, setSortDir] = useState<SortDir>('desc')

  // F-RPT-N30 — Orders browser facets live in the URL so an Overview row can
  // arm "FoodPanda" and the drawer opens already filtered. Kept while stepping
  // between days; cleared by the banner / "Clear filters".
  const orderFilters = useMemo(() => filtersFromSearch(search), [search])
  const setOrderFilters = (next: OrdersBrowserFilters) => {
    navigate({
      to: '/admin/reports',
      // F-FE-N149: re-stamp the period the router never saw.
      search: (prev): ReportsSearch => ({ ...(prev as ReportsSearch), ...rangeToSearch(range), ...searchFromFilters(next) }),
      replace: true,
    })
  }
  const armedFilterCount = activeFilterCount(orderFilters)
  const { raw: orderTypeRows } = useEnabledOrderTypes()
  const armedFilterLabel = useMemo(() => {
    const parts: string[] = []
    if (orderFilters.orderTypes.length > 0) {
      parts.push(orderFilters.orderTypes.map((id) => displayLabelForOrderType(id, orderTypeRows)).join(' / '))
    }
    if (orderFilters.payment) {
      // F-MM-N24: capitalising the raw value renders 'gift_card' as
      // "Gift_card" on the armed-filter chip — the same raw-enum leak the
      // Tender mix legend had. Name it properly.
      const pay =
        orderFilters.payment === 'gift_card'
          ? 'Gift card'
          : orderFilters.payment === 'house_account'
            ? 'House account'
            : orderFilters.payment === 'staff_meal'
              ? 'Staff meal'
              : orderFilters.payment.charAt(0).toUpperCase() + orderFilters.payment.slice(1)
      parts.push(orderFilters.subMethod ? `${pay} · ${orderFilters.subMethod}` : pay)
    }
    if (orderFilters.status) {
      parts.push(
        orderFilters.status === 'credit_noted'
          ? 'Refunded'
          : orderFilters.status.charAt(0).toUpperCase() + orderFilters.status.slice(1),
      )
    }
    return parts.join(' · ')
  }, [orderFilters, orderTypeRows])

  const detailDay = useMemo(() => {
    const d = search.day
    if (!d || !/^\d{4}-\d{2}-\d{2}$/.test(d)) return null
    const dt = new Date(`${d}T00:00:00`)
    return Number.isNaN(dt.getTime()) ? null : dt
  }, [search.day])

  const openDrill = (iso: string) => {
    navigate({
      to: '/admin/reports',
      search: (prev): ReportsSearch => ({
        ...(prev as ReportsSearch),
        // F-FE-N149: the router never saw the period (raw replaceState),
        // so re-stamp it or this navigation strips it from the URL.
        ...rangeToSearch(range),
        tab: 'daily' as ReportTab,
        day: iso,
        // Manual day-open should never auto-open a stale invoice from a prior
        // search.
        order: undefined,
      }),
      replace: true,
    })
  }

  const closeDrill = () => {
    navigate({
      to: '/admin/reports',
      search: (prev): ReportsSearch => {
        const { day: _omitDay, order: _omitOrder, ...rest } = prev as ReportsSearch
        // F-FE-N149: closing the drawer must not drop the period either.
        return { ...rest, ...rangeToSearch(range) }
      },
      replace: true,
    })
  }

  const setDrillDay = (d: Date) => {
    navigate({
      to: '/admin/reports',
      search: (prev): ReportsSearch => ({
        ...(prev as ReportsSearch),
        // F-FE-N149: the router never saw the period (raw replaceState),
        // so re-stamp it or this navigation strips it from the URL.
        ...rangeToSearch(range),
        tab: 'daily' as ReportTab,
        day: toIsoDate(d),
        // Stepping to another day clears any invoice deep-link.
        order: undefined,
      }),
      replace: true,
    })
  }

  // Step a calendar day by ±1 — used by the in-sheet prev/next arrows so the
  // operator can move between days without closing the drawer. We clamp the
  // forward arrow at today's calendar date.
  const shiftDrillDay = (offset: number) => {
    if (!detailDay) return
    const next = new Date(detailDay)
    next.setDate(next.getDate() + offset)
    next.setHours(0, 0, 0, 0)
    setDrillDay(next)
  }
  const todayIso = useMemo(() => toIsoDate(new Date()), [])
  const nextDisabled = useMemo(() => {
    if (!detailDay) return true
    const next = new Date(detailDay)
    next.setDate(next.getDate() + 1)
    return toIsoDate(next) > todayIso
  }, [detailDay, todayIso])

  const dailyQuery = useQuery({
    queryKey: ['reports-v2', 'daily', range.fromISO, range.toISO],
    queryFn: () => apiClient.getDailySalesReport(range.fromISO, range.toISO),
    staleTime: 30_000,
  })

  const allRows = dailyQuery.data?.data ?? []

  const detailRow = useMemo(() => {
    if (!detailDay) return null
    const iso = toIsoDate(detailDay)
    return allRows.find((r) => r.date === iso) ?? null
  }, [allRows, detailDay])

  // The same search box doubles as an invoice finder: when the operator pastes
  // an invoice # / IRN / customer, we search orders across ALL days (the days
  // table below still filters by date). Debounced so we don't hit the server
  // on every keystroke; only runs for queries of 2+ chars.
  const [invoiceQ, setInvoiceQ] = useState('')
  useEffect(() => {
    const t = setTimeout(() => setInvoiceQ(sortSearch.trim()), 300)
    return () => clearTimeout(t)
  }, [sortSearch])

  const invoiceSearchQuery = useQuery({
    queryKey: ['reports-v2', 'invoice-search', invoiceQ],
    queryFn: () => apiClient.searchInvoices(invoiceQ),
    enabled: invoiceQ.length >= 2,
    staleTime: 15_000,
  })
  const invoiceMatches: InvoiceSearchRow[] =
    invoiceSearchQuery.data?.data?.results ?? []
  // A digit in the query signals "this is probably an invoice/IRN" — only then
  // do we surface a "no match" message, so plain date typing stays quiet.
  const invoiceQLooksLikeId = /\d/.test(invoiceQ)
  const showInvoicePanel =
    invoiceQ.length >= 2 &&
    (invoiceSearchQuery.isFetching ||
      invoiceMatches.length > 0 ||
      invoiceQLooksLikeId)

  // Open a matched invoice in the existing day drill-in drawer (full receipt,
  // reprint, credit note) by deep-linking to its business day + order id.
  const openInvoice = (row: InvoiceSearchRow) => {
    navigate({
      to: '/admin/reports',
      search: (prev): ReportsSearch => ({
        ...(prev as ReportsSearch),
        // F-FE-N149: the router never saw the period (raw replaceState),
        // so re-stamp it or this navigation strips it from the URL.
        ...rangeToSearch(range),
        tab: 'daily' as ReportTab,
        day: row.business_date,
        order: row.id,
        // A found invoice must never be hidden by an armed facet.
        ...searchFromFilters(clearFilters(filtersFromSearch(prev as ReportsSearch))),
      }),
      replace: true,
    })
  }

  const sorted = useMemo(() => {
    const q = sortSearch.trim().toLowerCase()
    const filtered = q
      ? allRows.filter((r) => {
          // Allow searching by ISO date, the legacy DD-MM-YYYY label still
          // shipped from the backend, and the human form rendered in the UI
          // — so a manager can type "fri", "9 may", or "09" and find rows.
          const human = formatDateHuman(r.date).toLowerCase()
          return (
            r.date_label.toLowerCase().includes(q) ||
            r.date.toLowerCase().includes(q) ||
            human.includes(q)
          )
        })
      : allRows
    const dir = sortDir === 'asc' ? 1 : -1
    const copy = [...filtered]
    const valueFor = (r: DailySalesRow): string | number => {
      switch (sortKey) {
        case 'date':
          return r.date
        default:
          return (r as unknown as Record<string, number>)[sortKey]
      }
    }
    copy.sort((a, b) => {
      const av = valueFor(a)
      const bv = valueFor(b)
      if (typeof av === 'string' && typeof bv === 'string') return av.localeCompare(bv) * dir
      return ((av as number) - (bv as number)) * dir
    })
    return copy
  }, [allRows, sortSearch, sortKey, sortDir])

  const totals = useMemo(() => {
    return sorted.reduce(
      (acc, r) => ({
        orders: acc.orders + r.orders,
        covers: acc.covers + r.covers,
        subtotal: acc.subtotal + r.subtotal,
        gross: acc.gross + r.gross,
        discounts: acc.discounts + r.discounts,
        serviceCharge: acc.serviceCharge + (r.service_charge ?? 0),
        net: acc.net + r.net,
        tax: acc.tax + r.tax,
      }),
      {
        orders: 0,
        covers: 0,
        subtotal: 0,
        gross: 0,
        discounts: 0,
        serviceCharge: 0,
        net: 0,
        tax: 0,
      },
    )
  }, [sorted])

  const toggleSort = (k: SortKey) => {
    if (sortKey === k) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    else {
      setSortKey(k)
      setSortDir(k === 'date' ? 'desc' : 'desc')
    }
  }

  const headerSort = (label: string, k: SortKey, alignRight = false) => (
    <button
      type="button"
      onClick={() => toggleSort(k)}
      className={`group inline-flex items-center gap-1 text-xs font-medium uppercase tracking-wide text-muted-foreground hover:text-foreground ${alignRight ? 'justify-end w-full' : ''}`}
    >
      {label}
      {sortKey === k ? (
        sortDir === 'asc' ? (
          <ArrowUp className="w-3 h-3" />
        ) : (
          <ArrowDown className="w-3 h-3" />
        )
      ) : (
        <ArrowUpDown className="w-3 h-3 opacity-40 group-hover:opacity-80" />
      )}
    </button>
  )

  const brand = useBusinessNameWithFallback()
  const handlePrintPdf = () => {
    openPrintableReport({
      title: 'Daily Sales',
      subtitle: `${formatDateRangeHuman(range.from, range.to)} • Asia/Karachi`,
      bodyHtml: buildDailyPdf(sorted, totals, formatCurrency),
      brand,
    })
  }

  // The "Today" preset always shows the calendar date the user is looking at,
  // but the row totals are bucketed by business day (orders placed before the
  // configured day_boundary_hour roll back into yesterday — same logic the
  // Day Close drawer uses). When the user opens Reports between midnight and
  // the boundary, the discrepancy can look like a bug ("I just rang up two
  // sales but Today shows 0"), so surface a one-line explanation.
  const dayBoundaryHour = useDayBoundaryHour()
  const showLateNightHint = useMemo(() => {
    if (range.preset !== 'today') return false
    const localHour = new Date().getHours()
    return localHour >= 0 && localHour < dayBoundaryHour
  }, [range.preset, dayBoundaryHour])
  const boundaryLabel = formatHour12(dayBoundaryHour)

  return (
    <div className="space-y-4">
      {armedFilterCount > 0 && !detailDay && (
        <div className="flex flex-wrap items-center gap-2 rounded-md border border-border bg-muted/30 px-3 py-2 text-sm">
          <Filter className="h-4 w-4 text-muted-foreground" />
          <span>
            Order filter armed: <span className="font-medium">{armedFilterLabel}</span>
            <span className="text-muted-foreground"> — applies when you open a day.</span>
          </span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="ml-auto h-7 gap-1 text-xs"
            onClick={() => setOrderFilters(clearFilters(orderFilters))}
          >
            <X className="h-3.5 w-3.5" /> Clear
          </Button>
        </div>
      )}
      <div className="relative max-w-sm w-full">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Search a day, or an invoice # / IRN"
          value={sortSearch}
          onChange={(e) => setSortSearch(e.target.value)}
          className="pl-9"
        />
      </div>
      <ReportsExportSlot>
        <ExportButton
          report="daily_sales"
          reportLabel="Daily sales"
          fromISO={range.fromISO}
          toISO={range.toISO}
          onPrintPdf={handlePrintPdf}
          onOrderDetailExport={() => apiClient.exportOrdersAuditXlsx(range.fromISO, range.toISO)}
        />
      </ReportsExportSlot>

      {showInvoicePanel && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">
              Matching invoices
              {invoiceMatches.length > 0 ? ` (${invoiceMatches.length})` : ''}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            {invoiceSearchQuery.isFetching && invoiceMatches.length === 0 ? (
              <div className="space-y-2">
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
              </div>
            ) : invoiceMatches.length === 0 ? (
              <p className="py-2 text-sm text-muted-foreground">
                No invoice matches &ldquo;{invoiceQ}&rdquo;.
              </p>
            ) : (
              <div className="divide-y divide-border">
                {invoiceMatches.map((row) => (
                  <button
                    key={row.id}
                    type="button"
                    onClick={() => openInvoice(row)}
                    className="flex w-full flex-wrap items-center gap-x-3 gap-y-0.5 rounded-md px-2 py-2 text-left text-sm transition-colors hover:bg-muted/50"
                  >
                    <span className="font-semibold tabular-nums">
                      {row.order_number}
                    </span>
                    <span className="text-muted-foreground">
                      {formatDateHuman(row.business_date)}
                    </span>
                    {row.customer_name && (
                      <span className="truncate text-muted-foreground">
                        {row.customer_name}
                      </span>
                    )}
                    <span className="ml-auto font-medium tabular-nums">
                      {formatCurrency(row.total_amount)}
                    </span>
                    {(row.fiscal_irn || row.pra_invoice_number) && (
                      <span className="w-full text-xs tabular-nums text-muted-foreground">
                        {row.fiscal_irn
                          ? `IRN ${row.fiscal_irn}`
                          : `Invoice ${row.pra_invoice_number}`}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {showLateNightHint && (
        <div
          role="note"
          className="flex items-start gap-2 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-900 dark:border-blue-900/40 dark:bg-blue-950/40 dark:text-blue-200"
        >
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>
            Late-night orders before <span className="font-medium">{boundaryLabel}</span> are
            counted in the previous business day, matching Day Close. Switch to{' '}
            <button
              type="button"
              className="underline-offset-2 hover:underline font-medium"
              onClick={() => range.setPreset('yesterday')}
            >
              Yesterday
            </button>{' '}
            to see them.
          </span>
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            Daily sales — {formatDateRangeHuman(range.from, range.to)}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {dailyQuery.isLoading ? (
            <div className="p-6 space-y-2">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : sorted.length === 0 ? (
            <div className="p-10 text-center text-sm text-muted-foreground">
              No sales in this range.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[760px] text-sm">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left px-4 py-2.5">{headerSort('Date', 'date')}</th>
                    <th className="text-right px-4 py-2.5">{headerSort('Orders', 'orders', true)}</th>
                    <th className="text-right px-4 py-2.5">{headerSort('Guests', 'covers', true)}</th>
                    <th
                      className="text-right px-4 py-2.5"
                      title="SUM(subtotal): pre-discount, pre-tax line-item total"
                    >
                      {headerSort('Subtotal', 'subtotal', true)}
                    </th>
                    <th className="text-right px-4 py-2.5">{headerSort('Discounts', 'discounts', true)}</th>
                    <th className="text-right px-4 py-2.5" title="Inside Gross. Custom and waived charges are broken out in Discounts & Service Charge.">
                      {headerSort('Service charge', 'service_charge', true)}
                    </th>
                    <th
                      className="text-right px-4 py-2.5"
                      title="SUM(total_amount): post-discount, tax-inclusive — what the customer paid. Matches the Admin Dashboard 'Gross sales' KPI."
                    >
                      {headerSort('Gross', 'gross', true)}
                    </th>
                    <th className="text-right px-4 py-2.5">{headerSort('Tax', 'tax', true)}</th>
                    <th
                      className="text-right px-4 py-2.5"
                      title="Gross − Tax: revenue we actually earn (tax is a liability owed to FBR/PRA)"
                    >
                      {headerSort('Net', 'net', true)}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {sorted.map((row) => (
                    <DailyRow
                      key={row.date}
                      row={row}
                      formatCurrency={formatCurrency}
                      onDrillIn={() => openDrill(row.date)}
                    />
                  ))}
                </tbody>
                <tfoot className="bg-muted/30 font-semibold">
                  <tr>
                    <td className="px-4 py-3">Total</td>
                    <td className="text-right tabular-nums px-4 py-3">{totals.orders.toLocaleString('en-US')}</td>
                    <td className="text-right tabular-nums px-4 py-3">{totals.covers.toLocaleString('en-US')}</td>
                    <td className="text-right tabular-nums px-4 py-3">{formatCurrency(totals.subtotal)}</td>
                    <td className="text-right tabular-nums px-4 py-3">{formatCurrency(totals.discounts)}</td>
                    <td className="text-right tabular-nums px-4 py-3">{formatCurrency(totals.serviceCharge)}</td>
                    <td className="text-right tabular-nums px-4 py-3">{formatCurrency(totals.gross)}</td>
                    <td className="text-right tabular-nums px-4 py-3">{formatCurrency(totals.tax)}</td>
                    <td className="text-right tabular-nums px-4 py-3">{formatCurrency(totals.net)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Sheet
        open={!!detailDay}
        onOpenChange={(open) => {
          if (!open) closeDrill()
        }}
      >
        <SheetContent
          size="xl"
          aria-describedby={undefined}
          // Close button is provided by SheetContent; the day header below
          // still shows prev / next arrows + DayFilter so the user can
          // navigate without leaving the drawer.
          className="p-0"
        >
          {detailDay && (
            <>
              <SheetHeader className="gap-3">
                <div className="flex items-center justify-between gap-3 pr-9">
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => shiftDrillDay(-1)}
                      aria-label="Previous day"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <SheetTitle className="text-base">
                      {formatDateHuman(detailDay)}
                    </SheetTitle>
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => shiftDrillDay(1)}
                      disabled={nextDisabled}
                      aria-label="Next day"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                  <DayFilter day={detailDay} onChange={setDrillDay} />
                </div>
                {detailRow && (
                  <DaySummaryStrip row={detailRow} formatCurrency={formatCurrency} />
                )}
              </SheetHeader>
              <SheetBody>
                <OrdersBrowserTab
                  day={detailDay}
                  filters={orderFilters}
                  onFiltersChange={setOrderFilters}
                  initialOrderId={search.order}
                />
              </SheetBody>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  )
}

function DaySummaryStrip({
  row,
  formatCurrency,
}: {
  row: DailySalesRow
  formatCurrency: (n: number) => string
}) {
  // Avg ticket falls back to a dash when there are no orders, otherwise the
  // division renders "Rs ∞" / "NaN" and looks broken.
  const avgTicket = row.orders > 0 ? row.gross / row.orders : null
  const stats: { label: string; value: string; emphasised?: boolean }[] = [
    { label: 'Gross', value: formatCurrency(row.gross), emphasised: true },
    { label: 'Net', value: formatCurrency(row.net) },
    { label: 'Orders', value: row.orders.toLocaleString('en-US') },
    { label: 'Guests', value: row.covers > 0 ? row.covers.toLocaleString('en-US') : '—' },
    { label: 'Avg ticket', value: avgTicket != null ? formatCurrency(avgTicket) : '—' },
  ]
  return (
    <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
      {stats.map((s) => (
        <div key={s.label} className="rounded-md bg-muted/50 px-3 py-2">
          <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
            {s.label}
          </p>
          <p
            className={`tabular-nums text-sm ${
              s.emphasised ? 'font-semibold text-foreground' : 'text-foreground'
            }`}
          >
            {s.value}
          </p>
        </div>
      ))}
    </div>
  )
}

function DailyRow({
  row,
  formatCurrency,
  onDrillIn,
}: {
  row: DailySalesRow
  formatCurrency: (n: number) => string
  onDrillIn: () => void
}) {
  const label = formatDateHuman(row.date)
  // The whole <tr> is the click target; the chevron is an affordance only —
  // we keep it as a non-button span so screen readers don't announce a
  // redundant duplicate-action. Keyboard activation is handled on the row
  // via Enter/Space.
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTableRowElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      onDrillIn()
    }
  }
  return (
    <tr
      role="button"
      tabIndex={0}
      aria-label={`Open details for ${label}`}
      onClick={onDrillIn}
      onKeyDown={handleKeyDown}
      className="group cursor-pointer transition-colors hover:bg-muted/40 focus:outline-none focus-visible:bg-muted/40 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset"
    >
      <td className="px-4 py-3 font-medium tabular-nums">
        <div className="flex items-center gap-2">
          <span>{label}</span>
          <ChevronRight
            className="h-4 w-4 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-foreground"
            aria-hidden="true"
          />
        </div>
      </td>
      <td className="text-right tabular-nums px-4 py-3">{row.orders.toLocaleString('en-US')}</td>
      <td className="text-right tabular-nums px-4 py-3">{row.covers.toLocaleString('en-US')}</td>
      <td className="text-right tabular-nums px-4 py-3 text-muted-foreground">{formatCurrency(row.subtotal)}</td>
      <td className="text-right tabular-nums px-4 py-3 text-muted-foreground">
        {row.discounts > 0 ? formatCurrency(row.discounts) : '—'}
      </td>
      <td className="text-right tabular-nums px-4 py-3 text-muted-foreground">
        {(row.service_charge ?? 0) > 0 ? formatCurrency(row.service_charge ?? 0) : '—'}
      </td>
      <td className="text-right tabular-nums px-4 py-3">{formatCurrency(row.gross)}</td>
      <td className="text-right tabular-nums px-4 py-3 text-muted-foreground">{formatCurrency(row.tax)}</td>
      <td className="text-right tabular-nums px-4 py-3 font-semibold">{formatCurrency(row.net)}</td>
    </tr>
  )
}

function buildDailyPdf(
  rows: DailySalesRow[],
  totals: {
    orders: number
    covers: number
    subtotal: number
    gross: number
    discounts: number
    net: number
    tax: number
  },
  formatCurrency: (n: number) => string,
): string {
  const body = rows
    .map(
      (r) =>
        `<tr><td>${escapeHtml(formatDateHuman(r.date))}</td><td class="num">${r.orders}</td><td class="num">${r.covers}</td><td class="num">${escapeHtml(formatCurrency(r.subtotal))}</td><td class="num">${escapeHtml(formatCurrency(r.discounts))}</td><td class="num">${escapeHtml(formatCurrency(r.gross))}</td><td class="num">${escapeHtml(formatCurrency(r.tax))}</td><td class="num">${escapeHtml(formatCurrency(r.net))}</td></tr>`,
    )
    .join('')
  return `
    <table>
      <thead><tr>
        <th>Date</th><th class="num">Orders</th><th class="num">Guests</th><th class="num">Subtotal</th>
        <th class="num">Discounts</th><th class="num">Gross</th><th class="num">Tax</th><th class="num">Net</th>
      </tr></thead>
      <tbody>${body || `<tr><td colspan="8" class="muted">No sales</td></tr>`}</tbody>
      <tfoot><tr>
        <th>Total</th>
        <th class="num">${totals.orders}</th>
        <th class="num">${totals.covers}</th>
        <th class="num">${escapeHtml(formatCurrency(totals.subtotal))}</th>
        <th class="num">${escapeHtml(formatCurrency(totals.discounts))}</th>
        <th class="num">${escapeHtml(formatCurrency(totals.gross))}</th>
        <th class="num">${escapeHtml(formatCurrency(totals.tax))}</th>
        <th class="num">${escapeHtml(formatCurrency(totals.net))}</th>
      </tr></tfoot>
    </table>
  `
}
